import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { Center, Loader } from '@mantine/core';
import { Navigate, Route, Routes } from 'react-router-dom';
import { defaultRouteForRoles, Permission } from '@blagodar/shared';
import { useAuth } from './auth/AuthContext';
import { startSync } from './offline/sync';
import { AppLayout } from './components/AppLayout';
import { ScrollTopButton } from './components/ScrollTopButton';
import { HomeRedirect } from './components/HomeRedirect';
import { LoginPage } from './pages/LoginPage';

/**
 * Страницы грузятся по маршруту, а не одним куском.
 *
 * Сборка целиком весила больше полутора мегабайт, и планшет на мобильной
 * сети тянул её при первом входе десятки секунд — вместе со страницами
 * отчётов и зарплаты, которых замерщик не откроет никогда. Теперь в
 * главном чанке каркас и вход, остальное приезжает при открытии.
 *
 * Оффлайн от этого не страдает: в браузере service worker прекеширует
 * все чанки при установке, в APK файлы и так лежат в приложении. Чанк,
 * не доехавший из-за пропавшей сети, ловит PageErrorBoundary (правило 82).
 */
function lazyPage<T>(load: () => Promise<T>, pick: (m: T) => ComponentType) {
  return lazy(async () => {
    try {
      return { default: pick(await load()) };
    } catch (error) {
      // Перезагрузка уже пошла — ждём её, а не рисуем ошибку на миг
      if (reloadForStaleChunk()) return new Promise<never>(() => undefined);
      throw error;
    }
  });
}

/**
 * Страница, которой уже нет на сервере, — повод перезагрузиться, а не
 * ошибка (правило 211). Вкладка прошлой сборки просит страницу по старому
 * имени, а выкатка убирает старые файлы (правило 210): вместо «Экран не
 * открылся» вкладка один раз перезагружается и открывает ту же страницу
 * свежей сборкой. Второй сбой за минуту — уже не сборка, а сеть, и его
 * показывает PageErrorBoundary (правило 82).
 */
const CHUNK_RELOAD_KEY = 'blagodar.chunkReload';
function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

const TodayPage = lazyPage(() => import('./pages/TodayPage'), (m) => m.TodayPage);
const DealsPage = lazyPage(() => import('./pages/DealsPage'), (m) => m.DealsPage);
const NewLeadPage = lazyPage(() => import('./pages/NewLeadPage'), (m) => m.NewLeadPage);
const DealPage = lazyPage(() => import('./pages/DealPage'), (m) => m.DealPage);
const ClientPage = lazyPage(() => import('./pages/ClientPage'), (m) => m.ClientPage);
const InvoiceSheetPage = lazyPage(() => import('./pages/InvoiceSheetPage'), (m) => m.InvoiceSheetPage);
const WarrantySheetPage = lazyPage(() => import('./pages/WarrantySheetPage'), (m) => m.WarrantySheetPage);
const ReceiptSheetPage = lazyPage(() => import('./pages/ReceiptSheetPage'), (m) => m.ReceiptSheetPage);
const PaymentPlanSheetPage = lazyPage(
  () => import('./pages/PaymentPlanSheetPage'),
  (m) => m.PaymentPlanSheetPage,
);
const SchedulePage = lazyPage(() => import('./pages/SchedulePage'), (m) => m.SchedulePage);
const ScheduleBoardPage = lazyPage(
  () => import('./pages/ScheduleBoardPage'),
  (m) => m.ScheduleBoardPage,
);
const WeekBoardPage = lazyPage(() => import('./pages/WeekBoardPage'), (m) => m.WeekBoardPage);
const SpecificationSheetPage = lazyPage(
  () => import('./pages/SpecificationSheetPage'),
  (m) => m.SpecificationSheetPage,
);
const SheetLabelsPage = lazyPage(
  () => import('./pages/SheetLabelsPage'),
  (m) => m.SheetLabelsPage,
);
const DesignPage = lazyPage(() => import('./pages/DesignPage'), (m) => m.DesignPage);
const ProductionPage = lazyPage(() => import('./pages/ProductionPage'), (m) => m.ProductionPage);
const DesignProjectPage = lazyPage(
  () => import('./pages/DesignProjectPage'),
  (m) => m.DesignProjectPage,
);
const DesignSheetPage = lazyPage(() => import('./pages/DesignSheetPage'), (m) => m.DesignSheetPage);
const ProductionSheetPage = lazyPage(
  () => import('./pages/ProductionSheetPage'),
  (m) => m.ProductionSheetPage,
);
const MyMeasurementsPage = lazyPage(
  () => import('./pages/MyMeasurementsPage'),
  (m) => m.MyMeasurementsPage,
);
const PoolPage = lazyPage(() => import('./pages/PoolPage'), (m) => m.PoolPage);
const MeasurementFormPage = lazyPage(
  () => import('./pages/MeasurementFormPage'),
  (m) => m.MeasurementFormPage,
);
const SketchPadPage = lazyPage(() => import('./pages/SketchPadPage'), (m) => m.SketchPadPage);
const MeasurementSheetPage = lazyPage(
  () => import('./pages/MeasurementSheetPage'),
  (m) => m.MeasurementSheetPage,
);
const ContractSheetPage = lazyPage(
  () => import('./pages/ContractSheetPage'),
  (m) => m.ContractSheetPage,
);
const ActSheetPage = lazyPage(() => import('./pages/ActSheetPage'), (m) => m.ActSheetPage);
const PayslipPage = lazyPage(() => import('./pages/PayslipPage'), (m) => m.PayslipPage);
const InstallationsPage = lazyPage(
  () => import('./pages/InstallationsPage'),
  (m) => m.InstallationsPage,
);
const InstallersPage = lazyPage(() => import('./pages/InstallersPage'), (m) => m.InstallersPage);
const ReportsPage = lazyPage(() => import('./pages/ReportsPage'), (m) => m.ReportsPage);
const NonclosuresReportPage = lazyPage(() => import('./pages/NonclosuresReportPage'), (m) => m.NonclosuresReportPage);
const NonclosureCallsPage = lazyPage(() => import('./pages/NonclosureCallsPage'), (m) => m.NonclosureCallsPage);
const SurveyorsTodayPage = lazyPage(() => import('./pages/SurveyorsTodayPage'), (m) => m.SurveyorsTodayPage);
const ContractProfitPage = lazyPage(() => import('./pages/ContractProfitPage'), (m) => m.ContractProfitPage);
const HistoryPage = lazyPage(() => import('./pages/HistoryPage'), (m) => m.HistoryPage);
const MeasurersMonthPage = lazyPage(() => import('./pages/MeasurersMonthPage'), (m) => m.MeasurersMonthPage);
const HeadWorkPage = lazyPage(() => import('./pages/HeadWorkPage'), (m) => m.HeadWorkPage);
const LeadSourcesPage = lazyPage(() => import('./pages/LeadSourcesPage'), (m) => m.LeadSourcesPage);
const ReferencePage = lazyPage(() => import('./pages/ReferencePage'), (m) => m.ReferencePage);
const PayrollPage = lazyPage(() => import('./pages/PayrollPage'), (m) => m.PayrollPage);
const ConsumablesPage = lazyPage(() => import('./pages/ConsumablesPage'), (m) => m.ConsumablesPage);
const DaySheetPage = lazyPage(() => import('./pages/DaySheetPage'), (m) => m.DaySheetPage);
const ConsumablesSheetPage = lazyPage(
  () => import('./pages/ConsumablesSheetPage'),
  (m) => m.ConsumablesSheetPage,
);
const ConsumablesCardPage = lazyPage(
  () => import('./pages/ConsumablesCardPage'),
  (m) => m.ConsumablesCardPage,
);
const PickupDeskPage = lazyPage(() => import('./pages/PickupDeskPage'), (m) => m.PickupDeskPage);
const PickupDeskCardPage = lazyPage(() => import('./pages/PickupDeskPage'), (m) => m.PickupDeskCardPage);
const CatalogPage = lazyPage(() => import('./pages/CatalogPage'), (m) => m.CatalogPage);
const StaffPage = lazyPage(() => import('./pages/StaffPage'), (m) => m.StaffPage);
const DocumentsPage = lazyPage(() => import('./pages/DocumentsPage'), (m) => m.DocumentsPage);
const ContractSearchPage = lazyPage(() => import('./pages/ContractSearchPage'), (m) => m.ContractSearchPage);
const PaymentSwitchPage = lazyPage(() => import('./pages/PaymentSwitchPage'), (m) => m.PaymentSwitchPage);

export function App() {
  const { user, roles, can } = useAuth();

  // Фоновая отправка накопленных замеров работает, пока приложение открыто
  useEffect(() => {
    if (!user) return;
    return startSync();
  }, [user]);

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const home = defaultRouteForRoles(roles);
  // Склад забора без других прав на расходку (правило 292): роли
  // складываются, и тому, кто ещё и выдаёт, нужен обычный экран. Записано
  // списком, а не вызовами по одному: разбор стражей маршрутов в тестах
  // (`route-guards.ts`) читает вызовы права как стражу соседнего маршрута
  const pickupOnly = ![Permission.CONSUMABLES_FILL, Permission.WAREHOUSE_MANAGE, Permission.CONSUMABLES_VIEW_ALL].some(
    (p) => can(p),
  );

  return (
    <AppLayout>
      <Suspense
        fallback={
          <Center h="50vh">
            <Loader />
          </Center>
        }
      >
        <Routes>
        {/* Рабочий стол доступен всем вошедшим: набор карточек на нём
            собирается по правам на сервере, а не скрывается маршрутом */}
        <Route path="/today" element={<TodayPage />} />
        {/* ПРИКИДКА ПОТОЛКА — У ТЕХ, КТО ЧЕРТИТ.

            Экран ничего не открывает и никуда не пишет, и долго стоял
            вовсе без права: считалось, что «сколько выйдет на комнату
            три на четыре» спрашивают в ТРУБКУ, и нужен он колл-центру.
            Владелец 17.09.2026 сказал иначе: «по сути нужен только
            конструкторскому и замерщикам».

            ПРАВО И ПУНКТ МЕНЮ ЗАКРЫТЫ ОДИНАКОВО (правило 177): пункт,
            который видно, а нажать нельзя, возвращает человека на
            рабочий стол без единого слова — и это хуже отсутствующего
            пункта. */}
        {(can(Permission.DESIGN_VIEW) || can(Permission.MEASUREMENT_COMPLETE)) && (
          <Route path="/sketch" element={<SketchPadPage />} />
        )}
        <Route path="/" element={<HomeRedirect home={home} />} />
        <Route path="/login" element={<HomeRedirect home={home} />} />

        {can(Permission.DEAL_VIEW) && (
          <>
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/deals/:id" element={<DealPage />} />
            {/* Счёт собирается из сделки, а не из замера: остаток к оплате
                зависит от полученных денег, а они висят на сделке */}
            <Route path="/deals/:id/invoice" element={<InvoiceSheetPage />} />
            <Route path="/deals/:id/warranty" element={<WarrantySheetPage />} />
            <Route
              path="/deals/:id/receipt/:paymentId"
              element={<ReceiptSheetPage />}
            />
            <Route path="/deals/:id/payment-plan" element={<PaymentPlanSheetPage />} />
          </>
        )}
        {can(Permission.CLIENT_VIEW) && (
          <Route path="/clients/:id" element={<ClientPage />} />
        )}

        {can(Permission.DEAL_CREATE) && <Route path="/new-lead" element={<NewLeadPage />} />}
        {/* Запись, дневная сетка и неделя — у того, кто ЗАПИСЫВАЕТ.
            Право «видеть замеры» шире: оно есть у отдела документов и
            конструкторского, которым нужна смета и чертёж, а не час в
            графике замерщика. Пункт меню закрыт тем же правом
            (правило 177) */}
        {can(Permission.MEASUREMENT_SCHEDULE) && (
          <>
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/schedule/board" element={<ScheduleBoardPage />} />
            {/* Неделя одной сеткой — общее понимание, а не запись
                (правило 181). Под тем же правом, что и день */}
            <Route path="/schedule/week" element={<WeekBoardPage />} />
          </>
        )}
        {/* ПУЛ ОТКРЫТ И ТОМУ, КТО ЗАПИСЫВАЕТ.
            Карточка «Замер сорван: никто не поехал» приходит по праву
            записывать замеры — это его работа, позвонить заказчику
            (правило 131), — и ведёт она в пул. А маршрут стоял только
            под правом ЗАВЕРШАТЬ замеры, то есть у замерщика: оператор
            нажимал карточку и попадал обратно на рабочий стол, с
            которого нажимал. Та же поломка, что у правила 172, только
            причина не в отсутствующем маршруте, а в недоступном. */}
        {can(Permission.MEASUREMENT_SCHEDULE) && !can(Permission.MEASUREMENT_COMPLETE) && (
          <Route path="/pool" element={<PoolPage />} />
        )}
        {/* РАЗМЕТКА ОТКРЫТА И ЗАМЕРЩИКУ, И ОФИСУ.
            Свой почерк человек читает лучше всех, и лист, написанный его
            рукой, размечать ему; а пачку из офисной папки размечает тот,
            кто её принёс. Право здесь то же, что у ручек (правило 177):
            пункт меню, который нажимается в никуда, хуже отсутствующего */}
        {(can(Permission.MEASUREMENT_COMPLETE) || can(Permission.MEASUREMENT_VIEW_ALL)) && (
          <Route path="/sheet-labels" element={<SheetLabelsPage />} />
        )}
        {can(Permission.MEASUREMENT_COMPLETE) && (
          <>
            <Route path="/my-measurements" element={<MyMeasurementsPage />} />
            <Route path="/pool" element={<PoolPage />} />
            <Route path="/measurements/:id" element={<MeasurementFormPage />} />
          </>
        )}
        {/* ЛИСТЫ ЗАМЕРА — ТОМУ, КТО ИХ ПЕЧАТАЕТ, а не только замерщику.
            Договор, смета и спецификация стояли под правом ЗАВЕРШАТЬ
            замеры, и отдел документов — тот, кто их печатает и отдаёт
            заказчику, — нажимал «Договор» на своём экране и молча
            оказывался обратно на «Документах» (правило 177). Так было с
            21 августа: печатали из-под администратора. Данные листов
            отдаёт GET /measurements/:id под правом ВИДЕТЬ все замеры, им
            же листы и открыты; форма замера осталась замерщику */}
        {(can(Permission.MEASUREMENT_COMPLETE) || can(Permission.MEASUREMENT_VIEW_ALL)) && (
          <>
            <Route path="/measurements/:id/sheet" element={<MeasurementSheetPage />} />
            {/* Спецификация — «Приложение №2» в раскладке бланка компании:
                это и есть смета, которую подписывает заказчик (правило 190) */}
            <Route path="/measurements/:id/specification" element={<SpecificationSheetPage />} />
            <Route path="/measurements/:id/contract" element={<ContractSheetPage />} />
          </>
        )}
        {(can(Permission.PAYROLL_VIEW_OWN) || can(Permission.PAYROLL_VIEW_ALL)) && (
          <>
            <Route path="/payroll" element={<PayrollPage />} />
            {/* Свой листок без идентификатора, чужой — с ним */}
            <Route path="/payroll/slip" element={<PayslipPage />} />
            <Route path="/payroll/slip/:userId" element={<PayslipPage />} />
          </>
        )}
        {/* Расходка монтажника: заполняет бригада, выдаёт склад — оба права
            ведут на один экран (правило 177: экран открыт тому, кто получил
            на него ссылку) */}
        {(can(Permission.CONSUMABLES_FILL) ||
          can(Permission.WAREHOUSE_MANAGE) ||
          can(Permission.CONSUMABLES_VIEW_ALL)) && (
          <>
            <Route path="/consumables" element={<ConsumablesPage />} />
            {(can(Permission.CONSUMABLES_FILL) || can(Permission.WAREHOUSE_MANAGE)) && (
              <>
                <Route path="/consumables/new" element={<ConsumablesCardPage />} />
                <Route path="/consumables/:id" element={<ConsumablesCardPage />} />
                {/* Лист у окна выдачи: кладовщик собирает по бумаге */}
                <Route path="/consumables/:id/sheet" element={<ConsumablesSheetPage />} />
              </>
            )}
          </>
        )}
        {/* СКЛАД ЗАБОРА (правило 292) — свой список и своя карточка: только
            строки, которые основной склад отметил «выдадут на нашем складе»,
            и кнопка «Выдал». Чужих строк, сумм и выдачи там нет */}
        {can(Permission.CONSUMABLES_PICKUP) && pickupOnly && (
          <>
            <Route path="/consumables" element={<PickupDeskPage />} />
            <Route path="/consumables/:id" element={<PickupDeskCardPage />} />
          </>
        )}
        {can(Permission.INSTALLATION_VIEW) && (
          <>
            <Route path="/installations" element={<InstallationsPage />} />
            {/* Монтажники — отдельным разделом: состав бригад и занятость
                трогают каждый день, а справочники открывают раз в месяц */}
            <Route path="/installers" element={<InstallersPage />} />
            <Route path="/installations/:id/act" element={<ActSheetPage />} />
            {/* Дневной наряд: тот лист, который отдел документов собирал
                каждое утро руками */}
            <Route path="/installations/day-sheet" element={<DaySheetPage />} />
          </>
        )}
        {can(Permission.REPORT_VIEW) && <Route path="/reports" element={<ReportsPage />} />}
        {can(Permission.REPORT_VIEW) && <Route path="/reports/nonclosures" element={<NonclosuresReportPage />} />}
        {can(Permission.NONCLOSURE_CALLS) && <Route path="/reports/nonclosures/calls" element={<NonclosureCallsPage />} />}
        {/* Замеры дня по замерщикам и записи, которые ждут замерщика, —
            главному по замерщикам: смотреть, а не записывать (14.10.2026) */}
        {can(Permission.MEASUREMENT_OVERVIEW) && <Route path="/measurements/today" element={<SurveyorsTodayPage />} />}
        {can(Permission.REPORT_PROFIT) && <Route path="/reports/contract-profit" element={<ContractProfitPage />} />}
        {/* История договоров 1С за всё время по всем базам (правило 294): итоги — руководству,
            поиск договора — и тем, кто ищет договоры в 1С */}
        {(can(Permission.REPORT_PROFIT_TOTALS) || can(Permission.ONEC_CONTRACTS_SEARCH) || can(Permission.REPORT_PROFIT)) && (
          <Route path="/reports/history" element={<HistoryPage />} />
        )}
        {can(Permission.REPORT_VIEW) && <Route path="/reports/month" element={<MeasurersMonthPage />} />}
        {/* Откуда замеры — реклама КЦ и договор по источнику (правило 286) */}
        {can(Permission.REPORT_VIEW) && <Route path="/reports/lead-sources" element={<LeadSourcesPage />} />}
        {(can(Permission.REPORT_HEAD_WORK) || can(Permission.REPORT_HEAD_WORK_OWN)) && (
          <Route path="/reports/head-work" element={<HeadWorkPage />} />
        )}
        {/* Справочник замерщика — таблица компании листами как есть (правило 282) */}
        {can(Permission.REFERENCE_VIEW) && <Route path="/reference" element={<ReferencePage />} />}
        {can(Permission.CATALOG_MANAGE) && <Route path="/catalog" element={<CatalogPage />} />}
        {/* Отделу документов и сервису — одни монтажники (правило 281) */}
        {(can(Permission.USER_MANAGE) || can(Permission.USER_MANAGE_INSTALLERS)) && (
          <Route path="/staff" element={<StaffPage />} />
        )}

        {/* Этап 2 — конструкторский отдел */}
        {can(Permission.DESIGN_VIEW) && (
          <>
            <Route path="/design" element={<DesignPage />} />
            <Route path="/production" element={<ProductionPage />} />
            <Route path="/production/:id/sheet" element={<ProductionSheetPage />} />
            <Route path="/design/:dealId" element={<DesignProjectPage />} />
            {/* Смена формы оплаты: нал ↔ безнал по двум листам бланка (правило 293) */}
            <Route path="/payment-switch" element={<PaymentSwitchPage />} />
          </>
        )}

        {can(Permission.DOCUMENT_VIEW) && (
          <Route path="/documents" element={<DocumentsPage />} />
        )}
        {/* Договор во всех базах 1С — отделу документов: в сканах паспорта заказчиков (правило 288) */}
        {can(Permission.ONEC_CONTRACTS_SEARCH) && <Route path="/onec-contracts" element={<ContractSearchPage />} />}
        {/* Приложение №3 печатает и отдаёт заказчику отдел документов, а
            лист стоял под правом конструкторского — кнопка «Прил. №3» на
            «Документах» вела в никуда (правило 177). Лист только читает
            утверждённый проект; править проект по-прежнему может лишь
            конструкторский */}
        {(can(Permission.DESIGN_VIEW) || can(Permission.DOCUMENT_VIEW)) && (
          <Route path="/design/:dealId/sheet" element={<DesignSheetPage />} />
        )}

        {/* Ведёт на стартовый экран, но ровно один раз: см. HomeRedirect */}
        <Route path="*" element={<HomeRedirect home={home} />} />
        </Routes>
      </Suspense>
      <ScrollTopButton />
    </AppLayout>
  );
}
